import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Status,
  Textarea,
  Tooltip,
  formatDate,
  formatDateTime,
  formatRelative,
  segmentedItemClass,
  segmentedListClass,
  useToast,
} from '@buildbee/ui';
import { errorMessage, trpc, type RouterOutputs } from '../../lib/trpc';
import { actionDetail, actionLabel } from './nav';
import { AccountStatus } from './users';

type Detail = RouterOutputs['platform']['users']['get'];

const ROLE_NAMES: Record<string, string> = { owner: 'Owner', admin: 'Admin', pm: 'Project manager', sales: 'Sales / estimator', field: 'Field', sub: 'Subcontractor' };

/** /admin/users/$userId: one person's account, companies, sessions and support actions. */
export function PlatformUserPage({ userId }: { userId: string }) {
  const query = trpc.platform.users.get.useQuery({ id: userId });
  if (query.isLoading) return <LoadingState />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const u = query.data;
  return (
    <div className="space-y-6">
      <Link to="/admin/users" className="text-sm text-muted-foreground">
        ← All people
      </Link>
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            {u.name} <AccountStatus person={u} />
            {u.platformRole === 'super_admin' && <Badge variant="primary">Super admin</Badge>}
            {u.isSelf && <Badge variant="outline">You</Badge>}
          </CardTitle>
          <CardDescription>
            {u.email} · joined <span className="figures">{formatDate(u.createdAt)}</span> · signs in with {signInMethods(u)}
          </CardDescription>
        </CardHeader>
        {u.lockedAt && (
          <CardContent>
            <Alert variant="destructive" title={`Locked ${formatRelative(u.lockedAt)}`}>
              {u.lockedReason ?? 'No reason given.'} They can&apos;t sign in until you unlock the account.
            </Alert>
          </CardContent>
        )}
      </Card>
      <CompaniesCard u={u} />
      <div className="grid gap-6 lg:grid-cols-2">
        <PasswordCard u={u} />
        <SessionsCard u={u} />
      </div>
      <AccessCard u={u} />
      <HistoryCard u={u} />
    </div>
  );
}

function signInMethods(u: Detail): string {
  const names = u.providers.map((p) => (p === 'credential' ? 'password' : p === 'google' ? 'Google' : p === 'microsoft' ? 'Microsoft' : p));
  return names.length ? names.join(', ') : 'email link only';
}

function useDone() {
  const toast = useToast();
  const utils = trpc.useUtils();
  return {
    toast,
    done: (message: string) => {
      toast.success(message);
      void utils.platform.users.invalidate();
      void utils.platform.audit.invalidate();
    },
    onError: (err: unknown) => toast.error("That didn't work", errorMessage(err)),
  };
}

function CompaniesCard({ u }: { u: Detail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Companies</CardTitle>
        <CardDescription>Where this person works on BuildBee. Change their role from the company&apos;s Team page.</CardDescription>
      </CardHeader>
      <CardContent>
        {u.companies.length === 0 ? (
          <EmptyState title="Not on any company" description="They may be a client who only uses a client portal, or they haven't joined a team yet." />
        ) : (
          <ul className="divide-y divide-border text-sm">
            {u.companies.map((c) => (
              <li key={c.membershipId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <Link to="/admin/workspaces/$workspaceId/team" params={{ workspaceId: c.workspaceId }} className="font-medium hover:underline">
                  {c.workspaceName}
                </Link>
                <span className="flex items-center gap-3 text-muted-foreground">
                  {ROLE_NAMES[c.role] ?? c.role}
                  {c.status === 'suspended' ? <Status tone="blocked">Suspended</Status> : null}
                  {c.workspaceSuspendedAt ? <Status tone="risk">Company suspended</Status> : null}
                  <span>{c.lastActiveAt ? `Active ${formatRelative(c.lastActiveAt)}` : 'Never active'}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PasswordCard({ u }: { u: Detail }) {
  const { done, onError } = useDone();
  const [open, setOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [mode, setMode] = useState<'type' | 'generate'>('generate');
  const [password, setPassword] = useState('');
  const [verify, setVerify] = useState(false);
  const [generated, setGenerated] = useState<string | null>(null);
  const setPw = trpc.platform.users.setPassword.useMutation({
    onSuccess: (out) => {
      done('Password set');
      setPassword('');
      if (out.generatedPassword) setGenerated(out.generatedPassword);
      else setOpen(false);
    },
    onError,
  });
  const reset = trpc.platform.users.sendPasswordReset.useMutation({
    onSuccess: () => {
      done(`Reset email sent to ${u.email}`);
      setConfirmReset(false);
    },
    onError,
  });
  const isSuper = u.platformRole === 'super_admin';
  const close = () => {
    setOpen(false);
    setGenerated(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Password</CardTitle>
        <CardDescription>{u.hasPassword ? 'They have a password.' : 'No password yet: they sign in with an email link or Google/Microsoft.'}</CardDescription>
      </CardHeader>
      <CardFooter className="flex flex-wrap gap-2">
        <Tooltip content={isSuper ? "A super admin's password can only be changed with a reset email." : 'Type or generate a new password for them.'}>
          <span>
            <Button variant="outline" disabled={isSuper} onClick={() => setOpen(true)}>
              Set a new password
            </Button>
          </span>
        </Tooltip>
        <Button variant="ghost" onClick={() => setConfirmReset(true)}>
          Send reset email
        </Button>
      </CardFooter>

      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Send a password reset email?"
        description={`${u.email} gets a link to choose a new password. The link expires in one hour.`}
        confirmLabel="Send email"
        loading={reset.isPending}
        onConfirm={() => reset.mutate({ id: u.id })}
      />

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent title={generated ? 'New password' : `Set a new password for ${u.name}`} description={generated ? undefined : 'Tell them the new password yourself, over the phone or in person.'}>
          {generated ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <code className="figures flex-1 rounded-md bg-muted px-3 py-2 text-[15px]">{generated}</code>
                <Button variant="outline" onClick={() => void navigator.clipboard?.writeText(generated)}>
                  Copy
                </Button>
              </div>
              <Alert variant="warning">Copy it now. You won&apos;t see it again.</Alert>
              <div className="flex justify-end">
                <Button onClick={close}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div role="group" aria-label="How" className={segmentedListClass}>
                <button type="button" aria-pressed={mode === 'generate'} className={segmentedItemClass(mode === 'generate')} onClick={() => setMode('generate')}>
                  Generate one
                </button>
                <button type="button" aria-pressed={mode === 'type'} className={segmentedItemClass(mode === 'type')} onClick={() => setMode('type')}>
                  Type one
                </button>
              </div>
              {mode === 'type' && (
                <Field label="New password" htmlFor="pw-new" description="At least 10 characters.">
                  <Input id="pw-new" type="text" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} />
                </Field>
              )}
              <p className="text-sm text-muted-foreground">This also signs them out everywhere, so anyone using the old password is out.</p>
              {!u.emailVerified && <Checkbox checked={verify} onCheckedChange={(v) => setVerify(v === true)} label="Also mark their email as verified" />}
              <div className="flex justify-end">
                <Button
                  disabled={mode === 'type' && password.length < 10}
                  loading={setPw.isPending}
                  onClick={() => setPw.mutate({ id: u.id, ...(mode === 'generate' ? { generate: true } : { password }), markEmailVerified: verify })}
                >
                  {mode === 'generate' ? 'Generate password' : 'Save password'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SessionsCard({ u }: { u: Detail }) {
  const { done, onError } = useDone();
  const [confirm, setConfirm] = useState(false);
  const revoke = trpc.platform.users.revokeSessions.useMutation({
    onSuccess: (out) => {
      done(out.revoked === 1 ? 'Signed out of 1 session' : `Signed out of ${out.revoked} sessions`);
      setConfirm(false);
    },
    onError,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Signed in</CardTitle>
        <CardDescription>{u.sessions.length ? `${u.sessions.length} active session${u.sessions.length === 1 ? '' : 's'}.` : 'Not signed in anywhere.'}</CardDescription>
      </CardHeader>
      {u.sessions.length > 0 && (
        <CardContent>
          <ul className="space-y-2 text-sm">
            {u.sessions.slice(0, 8).map((s) => (
              <li key={s.id} className="flex flex-wrap justify-between gap-2">
                <span className="truncate text-muted-foreground">{shortAgent(s.userAgent)}</span>
                <span className="figures">
                  {s.ipAddress ?? 'unknown IP'} · {formatRelative(s.updatedAt)}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      )}
      <CardFooter>
        <Tooltip content={u.isSelf ? "You can't sign yourself out from here. Use Sign out." : 'Ends every session on every device right away.'}>
          <span>
            <Button variant="outline" disabled={u.isSelf || u.sessions.length === 0} onClick={() => setConfirm(true)}>
              Sign out everywhere
            </Button>
          </span>
        </Tooltip>
      </CardFooter>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Sign ${u.name} out everywhere?`}
        description="Every device they're signed in on is signed out right away. They can sign in again."
        confirmLabel="Sign out everywhere"
        destructive
        loading={revoke.isPending}
        onConfirm={() => revoke.mutate({ id: u.id })}
      />
    </Card>
  );
}

function shortAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : /Expo|okhttp|Dalvik/i.test(ua) ? 'Mobile app' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

function AccessCard({ u }: { u: Detail }) {
  const { done, onError } = useDone();
  const [dialog, setDialog] = useState<'lock' | 'unlock' | 'grant' | 'remove' | 'verify' | null>(null);
  const [reason, setReason] = useState('');
  const closeAll = () => {
    setDialog(null);
    setReason('');
  };
  const lock = trpc.platform.users.lock.useMutation({ onSuccess: () => (done('Account locked'), closeAll()), onError });
  const unlock = trpc.platform.users.unlock.useMutation({ onSuccess: () => (done('Account unlocked'), closeAll()), onError });
  const role = trpc.platform.users.setPlatformRole.useMutation({ onSuccess: (_, v) => (done(v.role ? 'Now a super admin' : 'Super admin access removed'), closeAll()), onError });
  const verify = trpc.platform.users.setEmailVerified.useMutation({ onSuccess: (_, v) => (done(v.verified ? 'Email marked verified' : 'Email marked not verified'), closeAll()), onError });
  const isSuper = u.platformRole === 'super_admin';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account access</CardTitle>
        <CardDescription>Every change here is recorded in BuildBee&apos;s activity log with your name.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>
            Email {u.emailVerified ? 'verified' : 'not verified'}
            <span className="block text-muted-foreground">{u.emailVerified ? 'They can sign in with their password.' : "Password sign-in waits until the email is verified."}</span>
          </span>
          <Button variant="outline" size="sm" onClick={() => setDialog('verify')}>
            {u.emailVerified ? 'Mark not verified' : 'Mark verified'}
          </Button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>
            {isSuper ? 'Super admin' : 'Customer account'}
            <span className="block text-muted-foreground">{isSuper ? 'Can open this platform admin and act for any company.' : 'Sees only the companies they belong to.'}</span>
          </span>
          <Tooltip content={isSuper && u.isSelf ? "You can't remove your own super admin access." : !isSuper && (!u.emailVerified || u.lockedAt) ? 'Verify and unlock the account first.' : isSuper ? 'BuildBee always keeps at least one super admin.' : 'Gives full platform admin access.'}>
            <span>
              <Button variant="outline" size="sm" disabled={(isSuper && u.isSelf) || (!isSuper && (!u.emailVerified || !!u.lockedAt))} onClick={() => setDialog(isSuper ? 'remove' : 'grant')}>
                {isSuper ? 'Remove super admin' : 'Make super admin'}
              </Button>
            </span>
          </Tooltip>
        </div>
      </CardContent>
      <CardFooter className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
        <span className="text-sm text-muted-foreground">{u.lockedAt ? 'Unlocking lets them sign in again.' : 'Locking signs them out everywhere and stops every way of signing in.'}</span>
        {u.lockedAt ? (
          <Button variant="outline" onClick={() => setDialog('unlock')}>
            Unlock account
          </Button>
        ) : (
          <Tooltip content={u.isSelf ? "You can't lock your own account." : 'Stops all sign-ins for this person.'}>
            <span>
              <Button variant="destructive" disabled={u.isSelf} onClick={() => setDialog('lock')}>
                Lock account
              </Button>
            </span>
          </Tooltip>
        )}
      </CardFooter>

      <ConfirmDialog
        open={dialog === 'unlock'}
        onOpenChange={(o) => !o && closeAll()}
        title={`Unlock ${u.name}?`}
        description="They can sign in again straight away. Sessions from before the lock stay signed out."
        confirmLabel="Unlock account"
        loading={unlock.isPending}
        onConfirm={() => unlock.mutate({ id: u.id })}
      />
      <ConfirmDialog
        open={dialog === 'verify'}
        onOpenChange={(o) => !o && closeAll()}
        title={u.emailVerified ? 'Mark this email as not verified?' : 'Mark this email as verified?'}
        description={u.emailVerified ? 'They will need to verify the address again before signing in with a password.' : `Only do this if you're sure ${u.email} belongs to them.`}
        confirmLabel={u.emailVerified ? 'Mark not verified' : 'Mark verified'}
        destructive={u.emailVerified}
        loading={verify.isPending}
        onConfirm={() => verify.mutate({ id: u.id, verified: !u.emailVerified })}
      />
      <Dialog open={dialog === 'lock' || dialog === 'grant' || dialog === 'remove'} onOpenChange={(o) => !o && closeAll()}>
        <DialogContent
          size="sm"
          title={dialog === 'lock' ? `Lock ${u.name}'s account?` : dialog === 'grant' ? `Make ${u.name} a super admin?` : `Remove ${u.name}'s super admin access?`}
          description={
            dialog === 'lock'
              ? 'They are signed out everywhere and can’t sign in by password, email link, Google, Microsoft or Hive until you unlock the account.'
              : dialog === 'grant'
                ? 'They can open this platform admin, see every company and act for them.'
                : 'They go back to being a normal customer account.'
          }
          footer={
            <>
              <Button variant="outline" onClick={closeAll}>
                Cancel
              </Button>
              <Button
                variant={dialog === 'grant' ? 'primary' : 'destructive'}
                disabled={reason.trim().length < 3}
                loading={lock.isPending || role.isPending}
                onClick={() =>
                  dialog === 'lock' ? lock.mutate({ id: u.id, reason: reason.trim() }) : role.mutate({ id: u.id, role: dialog === 'grant' ? 'super_admin' : null, reason: reason.trim() })
                }
              >
                {dialog === 'lock' ? 'Lock account' : dialog === 'grant' ? 'Make super admin' : 'Remove access'}
              </Button>
            </>
          }
        >
          <Field label="Reason" htmlFor="ua-reason" required description="Saved in the activity log.">
            <Textarea id="ua-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function HistoryCard({ u }: { u: Detail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Support history</CardTitle>
        <CardDescription>What BuildBee staff changed for this person.</CardDescription>
      </CardHeader>
      <CardContent>
        {u.history.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {u.history.map((e) => (
              <li key={e.id} className="flex flex-wrap gap-x-3">
                <span className="figures text-muted-foreground">{formatDateTime(e.createdAt)}</span>
                <span className="font-medium">{actionLabel(e.action)}</span>
                <span className="text-muted-foreground">{actionDetail(e)}</span>
                <span className="text-muted-foreground">by {e.actorEmail}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
